/**
 * ErrorBoundary Component
 *
 * Captura erros de renderização React e exibe fallback.
 * Também reporta o erro via auto-report.
 *
 * @since 2026-03-26
 * @issue TH-49421
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo);

    // Disparar evento para o auto-report capturar
    window.dispatchEvent(new CustomEvent('api:server-error', {
      detail: {
        status: 0,
        url: window.location.pathname,
        method: 'RENDER',
        message: `[React Error] ${error.message} — ${errorInfo.componentStack?.slice(0, 500) || ''}`,
      },
    }));
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-lg w-full bg-card rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <i className="bi bi-exclamation-triangle text-red-600 dark:text-red-400 text-2xl" />
            </div>
            <h1 className="text-xl font-bold text-foreground mb-2">
              Algo deu errado
            </h1>
            <p className="text-muted-foreground mb-6">
              Ocorreu um erro inesperado. O problema foi reportado automaticamente.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Recarregar página
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
